import { ActionIcon, Button, FileInput, Group, Image, Modal, Stack, Text } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconPencil, IconPlus, IconTrash, IconUpload } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createBanner, deleteBanner, getListBanner, updateBanner } from 'src/apis/bannerAPI'
import { uploadProgress } from 'src/apis/uploadApi'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import TableComponent from 'src/components/TableComponent'
import { useLoading } from 'src/context/LoadingContext'
import { useModal } from 'src/context/ModalContext'
import { formatDateTimeMinus, size } from 'src/variable/constant'
import BannerSchedulePicker from './BannerSchedulePicker'

type BannerForm = {
	image_file: File | null
	image_url: string
	start_at: Date | null
	end_at: Date | null
}

const getStatus = (banner: any) => {
	const now = dayjs()
	if (now.isBefore(dayjs(banner.start_at))) return 'Scheduled'
	if (now.isBefore(dayjs(banner.end_at))) return 'Active'
	return 'Expired'
}

const waitForImageAvailability = async (imageUrl: string) => {
	const maxAttempts = 10
	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		const isAvailable = await new Promise<boolean>((resolve) => {
			const image = new window.Image()
			const cacheBuster = `banner-ready=${Date.now()}-${attempt}`
			image.onload = () => resolve(true)
			image.onerror = () => resolve(false)
			image.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}${cacheBuster}`
		})
		if (isAvailable) return
		await new Promise((resolve) => window.setTimeout(resolve, 1000))
	}

	throw new Error('Banner image is still processing. Please try again in a moment.')
}

function BannerPage() {
	const { openError, openModal } = useModal()
	const { openLoadingContext, closeLoadingContext } = useLoading()
	const [data, setData] = useState<any[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [editingBanner, setEditingBanner] = useState<any | null>(null)
	const [modalOpen, setModalOpen] = useState(false)
	const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 1, pageSize: 10 })

	const form = useForm<BannerForm>({
		initialValues: { image_file: null, image_url: '', start_at: null, end_at: null },
		validate: {
			image_url: (value) => value ? null : 'Banner image is required',
			start_at: (value) => value ? null : 'Start time is required',
			end_at: (value, values) => {
				if (!value) return 'End time is required'
				if (values.start_at && !dayjs(value).isAfter(dayjs(values.start_at))) return 'End time must be after start time'
				return null
			},
		},
		validateInputOnBlur: true,
	})

	const loadBanners = useCallback(async () => {
		setLoading(true)
		try {
			const response = await getListBanner({
				fields: ['$all'], page: pagination.pageIndex, limit: pagination.pageSize,
				order: [['start_at', 'DESC']],
			})
			setData(response?.data?.results?.objects?.rows || [])
			setTotal(response?.data?.results?.objects?.count || 0)
		} catch (error) {
			openError(error)
		} finally {
			setLoading(false)
		}
	}, [openError, pagination])

	useEffect(() => { loadBanners() }, [loadBanners])

	const closeEditor = useCallback(() => {
		setModalOpen(false)
		setEditingBanner(null)
		form.reset()
	}, [form])

	const openCreate = useCallback(() => {
		form.reset()
		setEditingBanner(null)
		setModalOpen(true)
	}, [form])

	const openEdit = useCallback((banner: any) => {
		setEditingBanner(banner)
		form.setValues({
			image_file: null,
			image_url: banner.image_url,
			start_at: new Date(banner.start_at),
			end_at: new Date(banner.end_at),
		})
		setModalOpen(true)
	}, [form])

	const submit = useCallback(async (values: BannerForm) => {
		openLoadingContext()
		try {
			const scheduleResponse = await getListBanner({
				fields: ['$all'],
				page: 1,
				limit: 3000,
			})
			const hasOverlap = (scheduleResponse?.data?.results?.objects?.rows || []).some(
				(banner: any) =>
					banner.id !== editingBanner?.id &&
					dayjs(values.start_at).isBefore(dayjs(banner.end_at)) &&
					dayjs(values.end_at).isAfter(dayjs(banner.start_at)),
			)
			if (hasOverlap) {
				form.setFieldError(
					'start_at',
					'Banner schedule overlaps an existing banner',
				)
				return
			}
			const imageUrl = values.image_file
				? await uploadProgress(values.image_file, undefined)
				: values.image_url
			if (values.image_file) {
				await waitForImageAvailability(imageUrl)
			}
			const body = {
				image_url: imageUrl,
				start_at: dayjs(values.start_at).startOf('minute').toISOString(),
				end_at: dayjs(values.end_at).startOf('minute').toISOString(),
			}
			const response = editingBanner
				? await updateBanner(editingBanner.id, body)
				: await createBanner(body)
			if (response?.data?.code === 200) {
				closeEditor()
				await loadBanners()
				openModal({ type: 'success', label: `Banner has been ${editingBanner ? 'updated' : 'created'} successfully` })
			}
		} catch (error) {
			openError(error)
		} finally {
			closeLoadingContext()
		}
	}, [
		closeEditor,
		closeLoadingContext,
		editingBanner,
		form,
		loadBanners,
		openError,
		openLoadingContext,
		openModal,
	])

	const remove = useCallback((banner: any) => {
		openModal({
			type: 'confirm', label: 'Do you want to delete this banner?',
			handleConfirm: async () => {
				openLoadingContext()
				try {
					const response = await deleteBanner(banner.id)
					if (response?.data?.code === 200) {
						await loadBanners()
						openModal({ type: 'success', label: 'Banner has been deleted successfully' })
					}
				} catch (error) {
					openError(error)
				} finally {
					closeLoadingContext()
				}
			},
		})
	}, [closeLoadingContext, loadBanners, openError, openLoadingContext, openModal])

	const columns = useMemo((): MRT_ColumnDef[] => [
		{ header: 'Image', accessorKey: 'image_url', size: size.text, Cell: ({ row }: any) => <Image src={row.original.image_url} alt="Banner" width={120} height={56} fit="cover" radius="sm" /> },
		{ header: 'Start at', accessorKey: 'start_at', size: size.text, Cell: ({ cell }: any) => dayjs(cell.getValue()).format(formatDateTimeMinus) },
		{ header: 'End at', accessorKey: 'end_at', size: size.text, Cell: ({ cell }: any) => dayjs(cell.getValue()).format(formatDateTimeMinus) },
		{ header: 'Status', accessorKey: 'status', size: size.no, Cell: ({ row }: any) => getStatus(row.original) },
	], [])

	return <Stack sx={{ width: '100%' }}>
		<Group position="apart">
			<Text size="sm" color="dimmed">Only one banner can be active at a time. All times use Asia/Ho_Chi_Minh.</Text>
			<CustomButton variant="outlined" onClick={openCreate}><Group spacing={6}><IconPlus size={18} /><span>Create banner</span></Group></CustomButton>
		</Group>
		<TableComponent
			columns={columns} total={total} data={data} pagination={pagination} loading={loading}
			handleChangePage={(page: number) => setPagination((previous) => ({ ...previous, pageIndex: page }))}
			_renderCustomActions={(row: any) => <Group spacing={8} noWrap>
				<ActionIcon variant="subtle" color="green" aria-label="Edit banner" title="Edit banner" onClick={() => openEdit(row.original)}>
					<IconPencil size={18} />
				</ActionIcon>
				<ActionIcon variant="subtle" color="red" aria-label="Delete banner" title="Delete banner" onClick={() => remove(row.original)}>
					<IconTrash size={18} />
				</ActionIcon>
			</Group>}
		/>
		<Modal
			opened={modalOpen}
			onClose={closeEditor}
			title={editingBanner ? 'Edit banner' : 'Create banner'}
			size={900}
			yOffset={16}
			styles={{ content: { minHeight: 'calc(100vh - 32px)' } }}
		>
			<form onSubmit={form.onSubmit(submit)}><Stack>
				<FileInput
					accept="image/png,image/jpeg,image/webp,image/gif"
					label="Banner image"
					withAsterisk
					icon={<IconUpload size={18} />}
					{...form.getInputProps('image_file')}
					error={form.errors.image_url}
					onChange={(file) => {
						form.setFieldValue('image_file', file)
						if (file) {
							form.setFieldValue('image_url', URL.createObjectURL(file))
							form.clearFieldError('image_url')
						}
					}}
				/>
				<BannerSchedulePicker
					startAt={form.values.start_at}
					endAt={form.values.end_at}
					startError={form.errors.start_at}
					endError={form.errors.end_at}
					onChange={({ startAt, endAt }) => {
						form.setFieldValue('start_at', startAt)
						form.setFieldValue('end_at', endAt)
						form.validateField('start_at')
						form.validateField('end_at')
					}}
				/>
				{form.values.image_url && <Image src={form.values.image_url} alt="Banner preview" height={160} fit="contain" radius="sm" />}
				<Button variant="outline" type="submit">{editingBanner ? 'Update banner' : 'Create banner'}</Button>
			</Stack></form>
		</Modal>
	</Stack>
}

export default React.memo(BannerPage)
